import { useEffect, useMemo, useState } from 'react';
import { api, ErroApi } from '../api';
import { numero } from '../formato';
import { Area, Campo, Dialogo, ErroDaApi, Rn } from '../ui';
import type { ItemCatalogo, Os } from './OsForms';

/**
 * 283 · revisar o escopo — um botão, uma janela (`RN-229`).
 *
 * «Hoje não consigo adicionar novos ensaios para uma OS já criada, apenas
 *  acrescentar mais ensaios ou reduzir ensaios (mas sempre o mesmo ensaio
 *  já cadastrado na emissão inicial da OS). Além disso acredito que não
 *  seriam necessários 2 botões para fazer essa revisão da OS» — Humberto,
 *  06/10/2026. Aprovado no canvas «Revisar o escopo» no mesmo dia.
 *
 * O que havia: dois botões («Incluir na OS» e «Revisar escopo») abrindo o
 * MESMO diálogo; a busca do catálogo escondida no fim da lista que rola;
 * a quantidade digitada era o ACRÉSCIMO; e reduzir só existia como
 * «retirar o item inteiro».
 *
 * O que fica: uma janela com todos os ensaios da OS, cada um com − / + da
 * QUANTIDADE NOVA; o mínimo é o que já está em uso (programado, na
 * bancada, aprovado ou marcado em amostra por chegar — o servidor confere
 * de novo, `RN-229`); zero tira do escopo; «＋ Adicionar ensaio» abre o
 * catálogo por grupo, navegável sem digitar; embaixo, o que muda e o
 * motivo. Antes do primeiro ensaio programado (`RN-218`, D2) a mesma janela
 * grava sem abrir revisão.
 */

interface Linha {
  itemMedicaoId: string;
  codigo: string;
  descricao: string;
  unidade: string;
  escopo: string | null;
  /** A quantidade de agora (0 para o ensaio que entra nesta revisão). */
  atual: number;
  /** O mínimo desta linha: o em uso do método, menos o que os outros itens do mesmo método cobrem. */
  minimo: number;
  emUso: number;
  novo: boolean;
}

const chato = (t: string | null | undefined) => (t ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const numeroDe = (v: string) => Number(v.replace(/\./g, '').replace(',', '.'));
const fmt = (n: number) => numero(n, 0, true);

export function JanelaRevisarEscopo({ os, adicionar = false, aoFechar, aoPronto }: {
  os: Os;
  /** Abre já com o catálogo à vista («Incluir na OS», vindo da programação). */
  adicionar?: boolean;
  aoFechar(): void;
  aoPronto(revisao: number | null): void;
}) {
  const livre = os.escopo_livre !== false;
  const nova = Number(os.revisao ?? 0) + 1;
  const itens = useMemo(() => os.escopo ?? [], [os.escopo]);
  const metodos = useMemo(() => os.escopo_por_metodo ?? [], [os.escopo_por_metodo]);

  const [catalogo, setCatalogo] = useState<ItemCatalogo[] | null>(null);
  const [texto, setTexto] = useState<Record<string, string>>({});
  const [novos, setNovos] = useState<string[]>([]);
  const [aberto, setAberto] = useState(adicionar);
  const [busca, setBusca] = useState('');
  const [motivo, setMotivo] = useState('');
  const [solicitante, setSolicitante] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const erroApi = erro instanceof ErroApi ? erro : null;

  useEffect(() => {
    const abortar = new AbortController();
    api<ItemCatalogo[]>('/itens-medicao', { sinal: abortar.signal })
      .then(setCatalogo)
      .catch((e) => { if ((e as Error).name !== 'AbortError') setCatalogo([]); });
    return () => abortar.abort();
  }, []);

  const doCatalogo = useMemo(
    () => new Map((catalogo ?? []).map((c) => [c.id, c])), [catalogo]);
  const porCodigo = useMemo(
    () => new Map((catalogo ?? []).map((c) => [c.codigo, c])), [catalogo]);

  /* A quantidade que vale para a conta: a digitada (se válida) ou a de agora. */
  const qtdDe = (id: string, atual: number) => {
    const t = texto[id];
    if (t === undefined || t.trim() === '') return atual;
    const n = numeroDe(t.trim());
    return Number.isFinite(n) && n >= 0 ? n : NaN;
  };

  /* O em uso de cada método (programados + marcados por chegar) e os itens dele. */
  const metodoDoItem = (codigo: string) => metodos.find((m) => (m.itens ?? []).includes(codigo));

  const linhas: Linha[] = useMemo(() => {
    const base: Linha[] = itens.map((i) => {
      const id = i.item_medicao_id ?? porCodigo.get(i.codigo)?.id ?? i.id;
      const m = metodoDoItem(i.codigo);
      const emUso = livre || !m ? 0 : Number(m.programados ?? 0) + Number(m.reservados ?? 0);
      return {
        itemMedicaoId: id, codigo: i.codigo, descricao: i.item, unidade: i.unidade,
        escopo: doCatalogo.get(id)?.escopo ?? porCodigo.get(i.codigo)?.escopo ?? null,
        atual: Number(i.quantidade_prevista), minimo: 0, emUso, novo: false,
      };
    });
    for (const id of novos) {
      const c = doCatalogo.get(id);
      if (!c) continue;
      base.push({
        itemMedicaoId: id, codigo: c.codigo, descricao: c.descricao, unidade: c.unidade_nome,
        escopo: c.escopo ?? null, atual: 0, minimo: 0, emUso: 0, novo: true,
      });
    }
    /* Dois itens do mesmo método dividem o em uso: o mínimo de cada um é o
       que os outros não cobrem. */
    for (const l of base) {
      if (!l.emUso) continue;
      const m = metodoDoItem(l.codigo);
      const outros = base.filter((x) => x !== l && m && (m.itens ?? []).includes(x.codigo))
        .reduce((s, x) => s + (Number.isNaN(qtdDe(x.itemMedicaoId, x.atual)) ? x.atual
          : qtdDe(x.itemMedicaoId, x.atual)), 0);
      l.minimo = Math.max(0, l.emUso - outros);
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itens, novos, doCatalogo, porCodigo, metodos, texto, livre]);

  const mudancas = linhas
    .map((l) => ({ l, q: qtdDe(l.itemMedicaoId, l.atual) }))
    .filter(({ l, q }) => !Number.isNaN(q) && q !== l.atual);
  const invalidas = linhas.filter((l) => Number.isNaN(qtdDe(l.itemMedicaoId, l.atual)));
  const abaixo = linhas.filter((l) => {
    const q = qtdDe(l.itemMedicaoId, l.atual);
    return !Number.isNaN(q) && q < l.minimo && q < l.atual;
  });
  const totalAntes = linhas.reduce((s, l) => s + l.atual, 0);
  const totalDepois = linhas.reduce((s, l) => {
    const q = qtdDe(l.itemMedicaoId, l.atual);
    return s + (Number.isNaN(q) ? l.atual : q);
  }, 0);
  const resumo = mudancas.map(({ l, q }) => (l.novo ? `+ ${l.codigo} ${fmt(q)}`
    : q === 0 ? `${l.codigo} sai` : `${l.codigo} ${fmt(l.atual)} → ${fmt(q)}`));
  const motivoOk = livre || motivo.trim().length >= 10;
  const pode = mudancas.length > 0 && invalidas.length === 0 && abaixo.length === 0 && motivoOk;
  const porqueNao = mudancas.length === 0 ? 'Nada mudou ainda.'
    : invalidas.length ? 'Há quantidade inválida.'
      : abaixo.length ? 'Há quantidade abaixo do que já está em uso.'
        : !motivoOk ? 'Escreva o que mudou e por quê (10 caracteres ou mais).' : '';

  const mudar = (l: Linha, v: number) => {
    if (l.novo && v <= 0) {
      setNovos((n) => n.filter((x) => x !== l.itemMedicaoId));
      setTexto((t) => { const u = { ...t }; delete u[l.itemMedicaoId]; return u; });
      return;
    }
    setTexto((t) => ({ ...t, [l.itemMedicaoId]: String(Math.max(0, v)) }));
  };

  /* O catálogo: o que ainda não está na janela, por grupo, com busca. */
  const naJanela = new Set(linhas.map((l) => l.itemMedicaoId));
  const q = chato(busca.trim());
  const grupos: Array<{ nome: string; itens: ItemCatalogo[] }> = [];
  for (const c of catalogo ?? []) {
    if (naJanela.has(c.id)) continue;
    if (q && !chato(`${c.codigo} ${c.descricao} ${c.categoria ?? ''}`).includes(q)) continue;
    const nome = (c.categoria ?? 'Outros').replace(/^Lab\./, '');
    const g = grupos.find((x) => x.nome === nome);
    if (g) g.itens.push(c); else grupos.push({ nome, itens: [c] });
  }

  async function gravar() {
    if (!pode) return;
    setErro(null); setEnviando(true);
    const lista = mudancas.map(({ l, q: n }) => ({ itemMedicaoId: l.itemMedicaoId, quantidade: n }));
    try {
      if (livre) {
        await api(`/os/${os.id}/escopo`, { metodo: 'PUT', corpo: { itens: lista } });
        aoPronto(null);
      } else {
        await api(`/os/${os.id}/revisoes`, {
          metodo: 'POST',
          corpo: {
            motivo: motivo.trim(),
            solicitante: solicitante.trim() || undefined,
            itensAlterados: lista,
          },
        });
        aoPronto(nova);
      }
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  }

  return (
    <Dialogo aberto larga rotulo={`OS ${os.numero}`}
      titulo={livre ? 'Revisar o escopo' : `Revisar o escopo · abre a revisão ${nova}`}
      aoFechar={aoFechar}>
      <div className="revisar-escopo">
        <ErroDaApi erro={erro} />
        <p className="re-aviso">
          {livre
            ? <>Nenhum ensaio desta OS foi programado ainda: o escopo muda sem abrir revisão
              (<Rn codigo="RN-218" />). O motivo é opcional.</>
            : <>A programação já começou: o que você gravar aqui abre a revisão {nova} da OS,
              com o motivo e quem pediu, e o PDF da OS sai na revisão nova. Nada é apagado — o
              escopo da revisão {Number(os.revisao ?? 0)} continua na história
              (<Rn codigo="RN-218" />).</>}
        </p>

        <div className="re-tabela" role="table" aria-label="Escopo contratado e a quantidade nova de cada ensaio">
          <div className="re-linha re-cab" role="row">
            <span role="columnheader">Ensaio</span>
            <span role="columnheader" className="num">{livre ? 'Agora' : `Na revisão ${Number(os.revisao ?? 0)}`}</span>
            <span role="columnheader" className="num">Em uso</span>
            <span role="columnheader">Quantidade nova</span>
            <span role="columnheader">O que muda</span>
          </div>
          {linhas.map((l) => {
            const v = qtdDe(l.itemMedicaoId, l.atual);
            const invalida = Number.isNaN(v);
            const d = invalida ? 0 : v - l.atual;
            const baixo = !invalida && v < l.minimo && v < l.atual;
            const classe = l.novo ? 'novo' : !invalida && v === 0 ? 'sai' : '';
            return (
              <div key={l.itemMedicaoId} className={`re-linha ${classe}`} role="row"
                data-codigo={l.codigo}>
                <span role="cell" className="re-ensaio">
                  <span className={`mini escopo-${l.escopo ?? 'outros'}`}>{l.codigo}</span>
                  <span className="re-desc">{l.descricao}</span>
                </span>
                <span role="cell" className="num mono">{l.novo ? '—' : fmt(l.atual)}</span>
                <span role="cell" className="num mono">{l.emUso ? fmt(l.emUso) : '—'}</span>
                <span role="cell" className="re-qtd">
                  <span className="re-passos">
                    <button type="button" className="re-passo" aria-label={`Diminuir ${l.codigo}`}
                      disabled={invalida || (!l.novo && v <= Math.min(l.minimo, l.atual)) || v <= 0}
                      onClick={() => mudar(l, (invalida ? l.atual : v) - 1)}>−</button>
                    <input className="re-valor mono" inputMode="decimal"
                      aria-label={`Quantidade nova de ${l.codigo}`}
                      aria-invalid={invalida || baixo}
                      value={texto[l.itemMedicaoId] ?? fmt(l.novo ? 1 : l.atual)}
                      onChange={(e) => setTexto((t) => ({ ...t, [l.itemMedicaoId]: e.target.value }))} />
                    <button type="button" className="re-passo" aria-label={`Aumentar ${l.codigo}`}
                      disabled={invalida}
                      onClick={() => mudar(l, (invalida ? l.atual : v) + 1)}>+</button>
                  </span>
                  <span className={`re-dica${baixo || invalida ? ' erro' : ''}`}>
                    {invalida ? 'quantidade inválida'
                      : baixo ? `mín. ${fmt(l.minimo)} — já em uso`
                        : !l.novo && l.minimo > 0 && v <= l.minimo ? `mín. ${fmt(l.minimo)} — já em uso`
                          : !l.novo && v === 0 ? 'sai do escopo' : ''}
                  </span>
                </span>
                <span role="cell">
                  {l.novo
                    ? <span className="re-muda ok">novo · +{fmt(invalida ? 0 : v)}</span>
                    : invalida || d === 0 ? <span className="re-muda">—</span>
                      : v === 0 ? <span className="re-muda critico">sai do escopo</span>
                        : <span className={`re-muda ${d > 0 ? 'ok' : 'atencao'}`}>
                          {d > 0 ? '+' : '−'}{fmt(Math.abs(d))} · {fmt(l.atual)} → {fmt(v)}
                        </span>}
                </span>
              </div>
            );
          })}
        </div>
        <p className="sub re-nota">
          Em uso = programado, na bancada, aprovado ou marcado em amostra por chegar. A quantidade
          não desce abaixo disso (<Rn codigo="RN-229" />); zero tira o ensaio do escopo.
        </p>

        {!aberto ? (
          <button type="button" className="botao secundario re-adicionar"
            onClick={() => setAberto(true)}>＋ Adicionar ensaio</button>
        ) : (
          <div className="re-catalogo" role="group" aria-label="Adicionar ensaio do catálogo">
            <div className="re-catalogo-topo">
              <Campo rotulo="Adicionar ensaio do catálogo — código, nome ou grupo"
                value={busca} onChange={(e) => setBusca(e.target.value)}
                dica="Ex.: triaxial, PERM, compactação. Ou role a lista por grupo."
                autoFocus={adicionar} />
              <button type="button" className="botao discreto" onClick={() => { setAberto(false); setBusca(''); }}>
                Fechar o catálogo
              </button>
            </div>
            <div className="re-catalogo-lista">
              {catalogo === null ? <p className="sub">Carregando o catálogo…</p>
                : grupos.length === 0
                  ? <p className="sub">{q ? <>Nada no catálogo com «{busca.trim()}».</> : 'Todos os itens do catálogo já estão no escopo.'}</p>
                  : grupos.map((g) => (
                    <div key={g.nome} className="re-grupo">
                      <div className="re-grupo-nome">{g.nome}</div>
                      {g.itens.map((c) => (
                        <button key={c.id} type="button" className="re-cat-item" data-codigo={c.codigo}
                          onClick={() => { setNovos((n) => [...n, c.id]); setTexto((t) => ({ ...t, [c.id]: '1' })); setBusca(''); }}>
                          <span className="re-ensaio">
                            <span className={`mini escopo-${c.escopo ?? 'outros'}`}>{c.codigo}</span>
                            <span className="re-desc">{c.descricao}</span>
                          </span>
                          <span className="re-cat-acao">Adicionar</span>
                        </button>
                      ))}
                    </div>
                  ))}
            </div>
          </div>
        )}

        <div className="re-motivo">
          <Area rotulo={livre ? 'O que mudou e por quê (opcional)' : 'O que mudou e por quê (obrigatório)'}
            rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            erro={erroApi?.campo('motivo')}
            dica="Ex.: cliente incluiu 1 triaxial CIU no ST-01 e retirou 2 umidades, conforme e-mail de 06/10." />
          {!livre && (
            <Campo rotulo="Quem pediu, no cliente" value={solicitante}
              onChange={(e) => setSolicitante(e.target.value)}
              erro={erroApi?.campo('solicitante')}
              dica="Nome de quem autorizou. Opcional, mas é o que responde «quem mandou» se a medição for contestada." />
          )}
        </div>

        <div className="re-resumo" aria-live="polite">
          <span className="re-resumo-rot">{livre ? 'O que muda' : `O que muda na revisão ${nova}`}</span>
          <span>{resumo.length ? resumo.join(' · ') : 'nada ainda'}</span>
          <span className="sub">{fmt(totalAntes)} → {fmt(totalDepois)} no escopo</span>
          {!pode && porqueNao && <span className="sub re-porque">{porqueNao}</span>}
        </div>
      </div>
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar} disabled={enviando}>Cancelar</button>
        <button type="button" className="botao" disabled={!pode || enviando} title={porqueNao || undefined}
          onClick={() => { void gravar(); }}>
          {enviando ? 'Gravando…' : livre ? 'Gravar o escopo' : `Gravar a revisão ${nova}`}
        </button>
      </div>
    </Dialogo>
  );
}
